import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import SisterSearcherFilters from "../components/SisterSearcherFilters";
import SisterSearcherPerformanceLegend from "../components/SisterSearcherPerformanceLegend";
import SisterSearcherPerformanceTable from "../components/SisterSearcherPerformanceTable";
import SisterSearcherVesselSelector from "../components/SisterSearcherVesselSelector";
import SisterSearcherVesselTable from "../components/SisterSearcherVesselTable";
import { useSisterSearcher } from "../hooks/useSisterSearcher";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  BeakerIcon,
  ChartBarSquareIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { twMerge } from "tailwind-merge";

// Reproduces the report's three bookmark states while retaining slicer state between transitions.
// API requests are the default; modeled fixtures are reachable only through the allowlisted Sidebar switch.
const SisterSearcher = () => {
  const theme = useAtomValue(themeAtom);
  const sisterSearcher = useSisterSearcher();
  const selectedVesselRows = sisterSearcher.searchResult?.selectedVessel
    ? [sisterSearcher.searchResult.selectedVessel]
    : [];
  const sisterVessels = sisterSearcher.searchResult?.sisterVessels ?? [];
  const performanceRows = [...sisterSearcher.performance].sort((left, right) =>
    left.imo.localeCompare(right.imo),
  );
  const selectedPerformanceRows = performanceRows.filter(
    (row) => row.imo === sisterSearcher.searchResult?.selectedVessel?.imo,
  );
  const sisterPerformanceRows = performanceRows.filter(
    (row) => row.imo !== sisterSearcher.searchResult?.selectedVessel?.imo,
  );

  const vesselSelector = (buttonLabel?: string) => (
    <SisterSearcherVesselSelector
      error={sisterSearcher.vesselError}
      onQueryChange={sisterSearcher.setVesselQuery}
      onSearch={sisterSearcher.openSisterSearch}
      onSelect={sisterSearcher.setSelectedImo}
      options={sisterSearcher.vesselOptions}
      query={sisterSearcher.vesselQuery}
      searchStatus={sisterSearcher.searchStatus}
      selectedImo={sisterSearcher.selectedImo}
      status={sisterSearcher.vesselStatus}
      buttonLabel={buttonLabel}
    />
  );

  return (
    <div className="flex h-screen" data-test="sister-searcher-page">
      <Sidebar />
      <div
        className={twMerge(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />
        <main className="flex-1 overflow-y-auto px-4 pb-6 pt-3 sm:px-6">
          <div className="mx-auto max-w-[1800px] space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-20 text-green dark:bg-teal-900/50">
                  <UsersIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-green">Fleet comparison</p>
                    {sisterSearcher.useMockData && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200" data-test="sister-searcher-mock-indicator">
                        <BeakerIcon className="h-3 w-3" /> Mock data
                      </span>
                    )}
                  </div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 sm:text-2xl">Sister Searcher</h1>
                </div>
              </div>
              {sisterSearcher.view === "main" && sisterSearcher.searchResult?.selectedVessel && (
                <button
                  type="button"
                  onClick={sisterSearcher.openPerformanceReview}
                  className="inline-flex items-center gap-2 rounded-lg bg-green px-4 py-2.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
                  data-test="sister-searcher-performance-review-button"
                >
                  Performance Review
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {sisterSearcher.view === "landing" && (
              <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800" data-test="sister-searcher-landing">
                <div className="grid items-center gap-8 bg-gradient-to-br from-primary-10 via-white to-teal-50 px-6 py-8 dark:from-teal-950/50 dark:via-gray-800 dark:to-gray-900 lg:grid-cols-[minmax(280px,0.8fr)_minmax(420px,1.2fr)]">
                  <div className="mx-auto flex w-full max-w-md items-center justify-center gap-4" aria-label="Two sister vessels">
                    {["Selected vessel", "Sister vessel"].map((label, index) => (
                      <div key={label} className={twMerge("relative h-52 w-40 rounded-[2rem] border-4 border-green/30 bg-gradient-to-b from-white to-primary-20 shadow-xl dark:from-gray-700 dark:to-teal-950", index === 1 && "mt-12")}>
                        <div className="absolute left-1/2 top-5 h-28 w-16 -translate-x-1/2 rounded-t-full bg-green/80" />
                        <div className="absolute bottom-9 left-4 right-4 h-5 rounded-full bg-teal-900/80" />
                        <p className="absolute bottom-2 w-full text-center text-[10px] font-semibold uppercase tracking-wide text-green">{label}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-green">Vessel selection</p>
                    <h2 className="mb-5 text-2xl font-semibold text-gray-900 dark:text-gray-100">Select Vessel by Name or IMO</h2>
                    {vesselSelector("Search Sister")}
                  </div>
                </div>
              </section>
            )}

            {sisterSearcher.view === "main" && (
              <>
                <div className="flex justify-start">
                  <button type="button" onClick={sisterSearcher.openLanding} className="inline-flex items-center gap-2 text-xs font-semibold text-green hover:text-teal-700" data-test="sister-searcher-main-back-button">
                    <ArrowLeftIcon className="h-4 w-4" /> Change selected vessel
                  </button>
                </div>
                {vesselSelector()}
                <SisterSearcherFilters
                  allSimilarityOptionsSelected={sisterSearcher.allSimilarityOptionsSelected}
                  inImos={sisterSearcher.inImos}
                  onInImosChange={sisterSearcher.setInImosSelection}
                  onToggleAllSimilarity={sisterSearcher.toggleAllSimilarityOptions}
                  onToggleSimilarity={sisterSearcher.toggleSimilarityOption}
                  onYearsAfterChange={sisterSearcher.setYearsAfter}
                  onYearsBeforeChange={sisterSearcher.setYearsBefore}
                  similarity={sisterSearcher.similarity}
                />

                {sisterSearcher.searchStatus === "loading" && (
                  <section className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-800" data-test="sister-searcher-search-loading">Loading sister vessels...</section>
                )}
                {sisterSearcher.searchStatus === "error" && (
                  <section className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200" data-test="sister-searcher-search-error">{sisterSearcher.searchError}</section>
                )}
                {sisterSearcher.searchResult?.selectedVessel && (
                  <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                      <ArrowsRightLeftIcon className="h-5 w-5 text-green" />
                      <div><h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Selected Vessel</h2><p className="text-[11px] text-gray-500 dark:text-gray-400">Reference vessel used for comparison</p></div>
                    </div>
                    <SisterSearcherVesselTable rows={selectedVesselRows} selected dataTest="sister-searcher-selected-vessel-table" />
                  </section>
                )}
                {sisterSearcher.searchStatus === "success" && !sisterSearcher.searchResult?.selectedVessel && (
                  <section className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-800">The selected vessel is no longer available. Choose another vessel and search again.</section>
                )}
                {sisterSearcher.searchResult?.selectedVessel && (
                  <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                      <div className="flex items-center gap-2"><UsersIcon className="h-5 w-5 text-green" /><div><h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Sister Vessels</h2><p className="text-[11px] text-gray-500 dark:text-gray-400">Vessels matching the selected characteristics, newest build first</p></div></div>
                      <span className="rounded-full bg-primary-10 px-3 py-1 text-xs font-semibold text-green dark:bg-teal-950/40">{sisterVessels.length} matches</span>
                    </div>
                    {sisterVessels.length > 0 ? <div className="max-h-[520px] overflow-auto"><SisterSearcherVesselTable rows={sisterVessels} dataTest="sister-searcher-sister-vessels-table" /></div> : <p className="px-4 py-10 text-center text-sm text-gray-500">No sister vessels match the selected filters.</p>}
                  </section>
                )}
              </>
            )}

            {sisterSearcher.view === "performance" && (
              <section className="space-y-4" data-test="sister-searcher-performance-view">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex items-start gap-2"><ChartBarSquareIcon className="mt-0.5 h-5 w-5 text-green" /><div><h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Performance Review</h2><p className="text-[11px] text-gray-500 dark:text-gray-400">ECO and FULL performance for the selected vessel and its sister vessels</p><div className="mt-2"><SisterSearcherPerformanceLegend /></div></div></div>
                  <button type="button" onClick={() => sisterSearcher.setView("main")} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:border-green hover:text-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200" data-test="sister-searcher-performance-back-button"><ArrowLeftIcon className="h-4 w-4" /> Back to Sister Searcher</button>
                </div>
                {sisterSearcher.performanceStatus === "loading" && <p className="rounded-xl border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">Loading performance review...</p>}
                {sisterSearcher.performanceStatus === "error" && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-12 text-center text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">{sisterSearcher.performanceError}</p>}
                {sisterSearcher.performanceStatus === "success" && performanceRows.length === 0 && <p className="rounded-xl border border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">No performance records are available for this result set.</p>}
                {selectedPerformanceRows.length > 0 && <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"><h3 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold dark:border-gray-700">Selected Vessel</h3><SisterSearcherPerformanceTable rows={selectedPerformanceRows} dataTest="sister-searcher-selected-performance-table" /></section>}
                {sisterPerformanceRows.length > 0 && <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"><h3 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold dark:border-gray-700">Sisters</h3><SisterSearcherPerformanceTable rows={sisterPerformanceRows} dataTest="sister-searcher-sisters-performance-table" /></section>}
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SisterSearcher;
